// packages/decision-ui/src/components/RecommendationLogic/RecommendationLogic.tsx

import React, { useMemo, useEffect } from 'react';

/**
 * RecommendationLogic - UI Shim Layer
 * 
 * This component wraps raw engine output and enhances it with A/A-Enhanced logic
 * and topology-based labeling (Y-Plan vs S-Plan) without modifying the core engine.
 * 
 * Key responsibilities:
 * 1. Apply <19kW rule for Y-Plan vs S-Plan labeling
 * 2. Separate "The Solution" (boiler) from "The Enablers" (filter, flush, controls)
 * 3. Generate A vs A-Enhanced comparison data
 * 4. Maintain engine output purity - only UI interpretation changes
 */

export interface EngineOutput {
  systemType: string;
  boilerName: string;
  efficiency: number;
  confidenceScore: number;
  heatLossKw?: number;
  propertySize?: number;
  includesFilter?: boolean;
  includesFlush?: boolean;
  includesWeatherComp?: boolean;
  includesZoning?: boolean;
}

export interface EnhancedRecommendation {
  // Original engine data
  engineOutput: EngineOutput;
  
  // UI enhancements
  topologyPlan: 'Y-Plan' | 'S-Plan' | 'W-Plan';
  topologyLabel: string;
  topologyRationale: string;
  
  // Full-upgrade vs minimum-upgrade breakdown (no costs - just scope)
  baseline: {
    label: 'Half measures (minimum)' | 'Minimum upgrade';
    scope: {
      boiler: boolean;
      flue: boolean;
      connection: boolean;
    };
    confidenceScore: number;
    risks: string[];
  };
  
  enhanced: {
    label: 'Full upgrade (recommended)' | 'Best-practice upgrade';
    scope: {
      boiler: boolean;
      flue: boolean;
      connection: boolean;
    };
    enablers: {
      filter: boolean;
      flush: boolean;
      controls: boolean;
      zoning: boolean;
    };
    confidenceScore: number;
    benefits: string[];
  };
}

export interface RecommendationLogicProps {
  engineOutput: EngineOutput;
  children?: (enhanced: EnhancedRecommendation) => React.ReactNode;
  onEnhanced?: (enhanced: EnhancedRecommendation) => void;
}

const RecommendationLogic: React.FC<RecommendationLogicProps> = ({
  engineOutput,
  children,
  onEnhanced,
}) => {
  // Apply UI-Shim logic - memoize to prevent infinite loops
  const enhanced = useMemo(() => applyUIShim(engineOutput), [engineOutput]);
  
  // Notify parent of enhanced data
  useEffect(() => {
    onEnhanced?.(enhanced);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enhanced]);
  
  // Render children with enhanced data
  if (children) {
    return <>{children(enhanced)}</>;
  }
  
  return null;
};

/**
 * Core UI-Shim Logic
 * Wraps engine output with UI-level interpretations
 */
function applyUIShim(engineOutput: EngineOutput): EnhancedRecommendation {
  const heatLossKw = engineOutput.heatLossKw || estimateHeatLoss(engineOutput);
  const propertySize = engineOutput.propertySize || estimatePropertySize(engineOutput);
  
  // Determine topology plan based on <19kW rule
  const topologyPlan = determineTopologyPlan(heatLossKw, propertySize);
  const topologyLabel = getTopologyLabel(topologyPlan);
  const topologyRationale = getTopologyRationale(topologyPlan, heatLossKw, propertySize);
  
  // Generate A vs A-Enhanced comparison (scope only, no costs)
  const baseline = generateBaselineOption(engineOutput);
  const enhanced = generateEnhancedOption(engineOutput);
  
  return {
    engineOutput,
    topologyPlan,
    topologyLabel,
    topologyRationale,
    baseline,
    enhanced,
  };
}

/**
 * Determine Y-Plan vs S-Plan vs W-Plan based on heat loss and property size
 */
function determineTopologyPlan(
  heatLossKw: number,
  propertySize: number
): 'Y-Plan' | 'S-Plan' | 'W-Plan' {
  // <19kW rule: Use Y-Plan for smaller systems
  if (heatLossKw < 19 || propertySize < 150) {
    return 'Y-Plan';
  } else if (heatLossKw < 35 || propertySize < 250) {
    return 'S-Plan';
  } else {
    return 'W-Plan';
  }
}

function getTopologyLabel(plan: 'Y-Plan' | 'S-Plan' | 'W-Plan'): string {
  switch (plan) {
    case 'Y-Plan':
      return 'Streamlined Shared Junction';
    case 'S-Plan':
      return 'Advanced Multi-Zone';
    case 'W-Plan':
      return 'Premium Multi-Zone Plus';
    default:
      return 'Standard';
  }
}

function getTopologyRationale(
  plan: 'Y-Plan' | 'S-Plan' | 'W-Plan',
  heatLossKw: number,
  propertySize: number
): string {
  switch (plan) {
    case 'Y-Plan':
      return `At ${heatLossKw}kW, a Y-Plan delivers maximum simplicity. On smaller systems, dual motorized valves add complexity without meaningful zoning benefits.`;
    case 'S-Plan':
      return `At ${heatLossKw}kW across ${propertySize}m², an S-Plan provides independent heating and hot water control for optimal efficiency.`;
    case 'W-Plan':
      return `At ${heatLossKw}kW across ${propertySize}m², a W-Plan offers premium multi-zone capability with hot water priority.`;
    default:
      return '';
  }
}

/**
 * Generate minimum-upgrade option - scope only, no costs
 */
function generateBaselineOption(engineOutput: EngineOutput) {
  // Baseline has lower confidence due to legacy system risks
  const baseConfidence = engineOutput.confidenceScore || 70;
  const adjustedConfidence = Math.max(60, baseConfidence - 15);
  
  return {
    label: 'Half measures (minimum)' as const,
    scope: {
      boiler: true,
      flue: true,
      connection: true,
    },
    confidenceScore: adjustedConfidence,
    risks: [
      '−20 if cylinder is NOT replaced where stored-DHW performance is required',
      '−15 if controls are NOT upgraded to modern compensating controls',
      '−30 if emitters are NOT upgraded for low-temperature heat-pump operation',
      'Existing sludge remains a threat to warranty and seasonal efficiency',
      'Limited zoning/control keeps comfort and efficiency below best-practice',
    ],
  };
}

/**
 * Generate full-upgrade (recommended) option - scope only, no costs
 */
function generateEnhancedOption(engineOutput: EngineOutput) {
  // Enhanced has high confidence (98%+) due to comprehensive protection
  const enhancedConfidence = 98;
  
  return {
    label: 'Full upgrade (recommended)' as const,
    scope: {
      boiler: true,
      flue: true,
      connection: true,
    },
    enablers: {
      filter: engineOutput.includesFilter ?? true,
      flush: engineOutput.includesFlush ?? true,
      controls: engineOutput.includesWeatherComp ?? true,
      zoning: engineOutput.includesZoning ?? true,
    },
    confidenceScore: enhancedConfidence,
    benefits: [
      'Best-practice package assumes critical system upgrades are completed',
      'System fully flushed and protected against debris',
      'Laminar flow filter prevents warranty voiding',
      'Weather compensation optimizes efficiency',
      'Advanced zoning maximizes comfort',
      'Full manufacturer warranty protection',
      '"Bulletproof" installation confidence',
    ],
  };
}

/**
 * Estimate heat loss if not provided by engine
 */
function estimateHeatLoss(engineOutput: EngineOutput): number {
  // Use system type to estimate
  if (engineOutput.systemType.toLowerCase().includes('combi')) {
    return 15; // Typical combi range
  } else if (engineOutput.systemType.toLowerCase().includes('system')) {
    return 22; // Typical system boiler range
  }
  return 18; // Default mid-range
}

/**
 * Estimate property size if not provided by engine
 */
function estimatePropertySize(engineOutput: EngineOutput): number {
  const heatLoss = engineOutput.heatLossKw || 18;
  // Rough estimate: 100W per m² = 0.1kW per m²
  return Math.round(heatLoss / 0.1);
}

export default RecommendationLogic;
